import { baseApiUrl } from 'src/config';
import { getAccessToken, getRealmName } from 'src/routes/AuthContext';

/**
 * BRK-1966: PDPM CMG estimator endpoint (contract per BRK-1963 / S6). Types
 * mirror the document-service DTOs field-for-field.
 *
 * The call is a POST because the what-if `overrides` map rides in the body, but
 * it is pure and idempotent server-side — no writes, safe to re-issue freely.
 */

/**
 * Where a row's value came from. Precedence (highest first): a derived-flag
 * override, an item-level override, the auto-pulled MDS/GG value, the analytics
 * fallback, then the MISSING default. A row the caller overrode directly comes
 * back as OVERRIDE; a derived row whose *inputs* were overridden stays DERIVED
 * and names them in `evidence.derivedFrom`.
 */
export type PdpmInputSource = 'OVERRIDE' | 'DERIVED' | 'AUTO' | 'ANALYTICS_FALLBACK' | 'MISSING';

/** A value this row accepts — the domain the backend 400s an override against. */
export type PdpmInputOption = { value: string; description: string };

/**
 * Which editor a row gets in the drawer (BRK-1969). Carried as a plain string on
 * the wire so a kind added by a newer backend degrades to read-only text instead
 * of failing to typecheck.
 */
export type PdpmInputValueType = 'GG_CODE' | 'BIMS' | 'CHECKBOX' | 'FLAG' | 'ENUM' | 'TEXT';

/** Which drawer section a row belongs to; see `sectionForInput` for the fallback. */
export type PdpmInputSection = 'CLINICAL_CATEGORY' | 'FUNCTION_SCORE' | 'SLP';

export type PdpmInputEvidence = {
  documentType: string | null;
  dateOfService: string | null;
  /** for a DERIVED row: the itemIds it was computed from */
  derivedFrom: string[] | null;
};

/**
 * One row of the full input roster: the 10 scored GG items + the GG0170I
 * walking gate, PRIMARY_DX, SURGICAL_HISTORY, C0500, COGNITIVE_IMPAIRMENT,
 * K0100A-D, SWALLOWING_DISORDER, K0520C3, MECH_ALTERED_DIET, the 12
 * COMORBIDITY_* flags and CLINICAL_CATEGORY. This array IS the inputs drawer;
 * override keys round-trip as `overrides[itemId] = value`.
 */
export type PdpmEstimateInput = {
  itemId: string;
  label: string;
  value: string | null;
  source: PdpmInputSource;
  /** whether this row is one of the rows counted by `completeness` */
  counted: boolean | null;
  /** editor kind; see PdpmInputValueType for the kinds the drawer recognizes */
  valueType: string | null;
  /** the override domain: what this row accepts, with descriptions for Selects */
  allowedValues: PdpmInputOption[] | null;
  /**
   * This row's contribution to the function score. Null for a row that does not
   * score and for a MISSING row — a missing GG item must never render as 0
   * points, which would read as "documented as fully dependent".
   */
  points: number | null;
  /** drawer section; the FE falls back to itemId when the backend omits it */
  section: string | null;
  evidence: PdpmInputEvidence | null;
};

export type PdpmPtOtCmg = {
  group: string;
  clinicalCategory: string | null;
  collapsedCategory: string | null;
  functionScore: number | null;
};

export type PdpmSlpCmg = {
  group: string;
  /** how many of the three criterion-A conditions are present (0-3) */
  criterionACount: number | null;
  criterionBLevel: string | null;
};

/** One GG function-score bucket (e.g. eating, toileting, the transfer average). */
export type PdpmFunctionBucket = {
  bucket: string;
  label: string | null;
  itemIds: string[];
  rawAverage: number | null;
  score: number | null;
};

/** Which SLP criteria fired — criterion A counts the first three, B the last two. */
export type PdpmSlpCriteria = {
  acuteNeurologic: boolean | null;
  slpComorbidity: boolean | null;
  cognitiveImpairment: boolean | null;
  swallowingDisorder: boolean | null;
  mechanicallyAlteredDiet: boolean | null;
};

export type PdpmDerivation = {
  buckets: PdpmFunctionBucket[];
  functionScoreRaw: number | null;
  functionScore: number | null;
  functionScoreBand: string | null;
  /** the next band up and what it would pay as, or null at the top band */
  nextBand: { atScore: number; group: string } | null;
  slpCriteria: PdpmSlpCriteria | null;
};

export type PdpmWarningSeverity = 'ERROR' | 'WARN' | 'INFO';

export type PdpmWarning = {
  code: string;
  severity: PdpmWarningSeverity;
  itemId?: string | null;
  message: string;
};

export type PdpmEstimateMeta = {
  fiscalYear: string | null;
  mappingVersion: string | null;
  anchorDate: string | null;
  engineVersion: string | null;
  calculatedAt: string | null;
};

/**
 * PARTIAL when a counted input is MISSING or the analytics fallback was used;
 * INSUFFICIENT when there is not enough data to group at all — in which case
 * `ptOtCmg`/`slpCmg` come back null and the caller renders the suppressed state.
 */
export type PdpmEstimateStatus = 'OK' | 'PARTIAL' | 'INSUFFICIENT';

export type PdpmEstimate = {
  status: PdpmEstimateStatus;
  ptOtCmg: PdpmPtOtCmg | null;
  slpCmg: PdpmSlpCmg | null;
  inputs: PdpmEstimateInput[];
  derivation: PdpmDerivation | null;
  warnings: PdpmWarning[];
  completeness: { present: number; total: number } | null;
  meta: PdpmEstimateMeta | null;
};

export type PdpmEstimateRequest = {
  patientUuid: string;
  /** 'YYYY-MM-DD'; omitted/null anchors the estimate on the facility's today */
  ard?: string | null;
  overrides?: Record<string, string>;
};

const headers = async () => ({
  Authorization: `Bearer ${await getAccessToken()}`,
  'x-tenant-id': getRealmName() || '',
});

const parseEnvelope = async (response: Response, fallback: string) => {
  let body: any = null;
  try {
    body = await response.json();
  } catch (e) {
    throw new Error(`${fallback} (HTTP ${response.status})`);
  }
  if (body?.code !== 'OK') {
    throw new Error(body?.message || fallback);
  }
  return body.data;
};

export const postPdpmEstimate = async (body: PdpmEstimateRequest): Promise<PdpmEstimate> => {
  const response = await fetch(`${baseApiUrl}/document/pdpm/estimate`, {
    method: 'POST',
    headers: { ...(await headers()), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseEnvelope(response, 'Failed to calculate the PDPM estimate');
};
